<template>
    <div
        :class="{ 'profile-link': clickable && user && 'hash' in user }"
        @click="onClick"
        :style="{ '--size': size }"
    >
        <div
            v-if="avatarId || avatarImage || avatarIcon"
            class="user-avatar user-avatar-image"
        >
            <!-- Display user avatar image if available -->
            <ImageDisplay
                v-if="avatarId"
                :image-id="avatarId"
                class="avatar-image"
            />

            <img
                v-else-if="avatarImage != ''"
                :src="require(`@/assets/images/${avatarImage}`)"
                class="avatar-image"
            />

            <div
                v-else-if="avatarIcon != ''"
                class="avatar-initials avatar-icon d-flex align-items-center justify-content-center"
            >
                <i class="fi" :class="avatarIcon"></i>
            </div>
        </div>
        <!-- Fallback to initials if no avatar -->
        <div v-else-if="user" class="user-avatar">
            <div
                class="avatar-initials d-flex align-items-center justify-content-center"
                style="background-color: white"
            >
                <span>{{ userInitials }}</span>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * @component UserAvatar
 * @description Displays user's avatar image or initials as fallback
 */
import ImageDisplay from '@/components/ImageDisplay.vue';

export default {
    name: 'UserAvatar',
    components: {
        ImageDisplay,
    },
    props: {
        user: Object,
        clickable: {
            type: Boolean,
            default: false,
        },
        avatarImage: {
            // explicitly specify an image URL - Following base path @/assets/images/
            type: String,
            default: '',
        },
        avatarIcon: {
            type: String,
            default: '',
        },
        size: {
            type: String,
            default: '2.5rem',
        },
    },
    emits: ['click'],

    data() {
        return {
            avatarId: null,
            userInitials: null,
            localUser: null,
        };
    },
    watch: {
        user: {
            handler(newValue, oldValue) {
                this.updateAvatar(newValue);
            },
            deep: true,
        },
    },
    methods: {
        onClick() {
            if (this.clickable && 'hash' in this.user) {
                this.$router.push({ path: `/team/${this.user.hash}` });
                this.$emit('click');
            }
        },

        /**
         * Gets the initial letter from a name
         * @param {string} name - Name to get initial from
         * @returns {string} Uppercase initial letter or empty string
         */
        getInitial(name) {
            if (!name) return '';
            return name.charAt(0).toUpperCase();
        },

        /**
         * Updates avatar display with user profile image or initials
         */
        updateAvatar(user) {
            this.localUser = user;

            if (!user) return;

            if (user.profile_image_id) {
                this.avatarId = user.profile_image_id;
            } else if (user?.first_name && user?.last_name) {
                this.userInitials =
                    this.getInitial(user.first_name) +
                    this.getInitial(user.last_name);
            } else {
                const [firstName = '', lastName = ''] = (user?.name || '').split(' ');
                this.userInitials = this.getInitial(firstName) + this.getInitial(lastName);
            }

            this.isLoading = false;
        },
    },
    mounted() {
        this.updateAvatar(this.user);
    },
    created() {
        this.updateAvatar(this.user);
    },
};
</script>

<style scoped>
.user-avatar {
    width: var(--size);
    min-width: var(--size);
    min-height: var(--size);
    height: var(--size);
    border-radius: 50%;
    overflow: hidden;
    display: relative;
}

.user-avatar-image {
    max-width: var(--size);
}

.avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.avatar-icon {
    background-color: var(--neutral-400);
    text-align: center;
}

.avatar-icon i {
    font-size: calc(var(--size) / 1.66);
    line-height: calc(var(--size) * 0.8);
}

.aimg {
    width: 2.5rem;
}

.avatar-initials {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    color: var(--neutral-900);
    border: 1px solid var(--neutral-600);
    font-size: calc(var(--size) / 2.5);
    font-weight: 600;
    line-height: calc(var(--size) * 0.8);
}

.profile-link:hover {
    cursor: pointer;
}
</style>
